import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  ArrowRightIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  EllipsisHorizontalIcon,
  ArrowPathIcon,
  ArrowRightStartOnRectangleIcon,
  CreditCardIcon,
  LanguageIcon,
  QuestionMarkCircleIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { useQueries, useQueryClient } from "@tanstack/react-query";
import useTranslation from "../../hooks/useTranslation";
import translations from "../../assets/translations.json";
import { useGeneratedTextPaginatedList } from "../../hooks/useGeneratedTextPaginatedList";
import { usePaginatedVocabularyList } from "../../hooks/usePaginatedVocabularyList";
import { useDailyTextCreate } from "../../hooks/useDailyTextCreate";
import { MVP_CAPABILITIES } from "../../mvpCapabilities";
import {
  type OrfDailyHeadline,
  useOrfDailyHeadlineQueries,
  useOrfHeadlineReadingTextCreate,
} from "../../hooks/useOrfDailyHeadlines";
import { primeGeneratedTextDetailsQuery } from "../../hooks/useGeneratedTextDetails";
import { useLanguageSpace } from "../../hooks/useLanguageSpace";
import { useLoginStatus } from "../../hooks/useLoginStatus";
import { useLogin } from "../../hooks/useLogin";
import { useUserSettings } from "../../hooks/useUserSettings";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import {
  deriveEffectiveLanguageSpaceScope,
  getAddedLanguageSpacesStorageKey,
} from "../../hooks/useAddedLanguageSpaces";
import {
  CONTINUE_READING_CTA_BY_LANGUAGE,
  ENABLED_LANGUAGE_SPACE_IDS,
  NATIVE_LANGUAGE_LABELS,
  READ_TODAY_CTA_BY_LANGUAGE,
  TEXT_LANGUAGES,
} from "../../constants";
import type { GeneratedText } from "../../hooks/types/GeneratedTexts";
import {
  getLocalIsoDate,
  getLocalIsoDateFromTimestamp,
} from "../../utils/getLocalIsoDate";
import { sortGeneratedTexts } from "../../utils/reepriseFeedOrdering";
import { getCsrfCookie } from "../../lib/utils";
import { notifySnackbar } from "../../lib/notifySnackbar";
import type {
  RabbitHoleWeek,
  RabbitHoleTopicSuggestion,
} from "../../hooks/useRabbitHole";
import {
  useRabbitHoleCreateWeek,
  RabbitHoleCreateError,
  useRabbitHoleRefreshTopicSuggestion,
  useRabbitHoleTopicSuggestionsBatch,
} from "../../hooks/useRabbitHole";
import {
  resolveOverviewDailyDisplayTitle,
  selectFeaturedOverviewDailyText,
} from "../../utils/overviewDailyTexts";
import { buildGeneratedTextTitleTranslationRequest } from "../../utils/generatedTextTitleTranslationRequest";
import {
  getResolvedGeneratedTextTitleCacheKey,
  type ResolvedGeneratedTextTitleCache,
} from "../../utils/generatedTextResolvedTitleCache";
import {
  findRabbitHoleDiscoveryForDate,
  isRabbitHoleDiscoveryRead,
  isRabbitHoleWeekActive,
  resolveEffectiveRabbitHoleDiscovery,
} from "../../utils/rabbitHoleDiscoveryState";
import {
  RabbitHoleEditionCover,
  RabbitHoleRecommendationCover,
} from "../../components/RabbitHoleEditionCover";
import { openRabbitHoleDiscovery } from "../../utils/openRabbitHoleDiscovery";
import {
  formatRabbitHoleEditionDateRange,
  estimateEditionTextWidth,
  getRabbitHoleEditionTitle,
} from "../../utils/rabbitHoleEditionDesign";
import {
  fetchWikipediaRefinement,
  getRabbitHoleTopicRefinement,
  type RabbitHoleTopicRefinement,
} from "../../utils/rabbitHoleTopicRefinement";

export const Route = createFileRoute("/app/")({
  component: AppOverviewPage,
});

const OVERVIEW_CHECKLIST_STORAGE_KEY = "app-overview-checklist-v1";
const OVERVIEW_TITLE_TRANSLATION_CACHE_KEY =
  "app-overview-title-translation-cache-v1";
const OVERVIEW_ORF_HEADLINE_LANGUAGE_STORAGE_KEY =
  "app-overview-orf-headline-language-v1";
export const OVERVIEW_NATIVE_LANGUAGE_LABELS = NATIVE_LANGUAGE_LABELS;

function AppOverviewPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { translate, language } = useTranslation();
  const loginStatus = useLoginStatus();
  const login = useLogin();
  const userSettings = useUserSettings({
    enabled: loginStatus.data?.state === "logged in",
  });
  const generatedTextList = useGeneratedTextPaginatedList();
  const personalEditionVocabulary = usePaginatedVocabularyList({
    enabled: loginStatus.data?.state === "logged in",
    word_languages: ["ITALIAN"],
    page_size: 30,
  });
  const dailyTextCreate = useDailyTextCreate();
  const { languageSpace, setLanguageSpace } = useLanguageSpace("ENGLISH");
  const currentUserId =
    loginStatus.data?.state === "logged in" ? loginStatus.data.userId : null;
  const personalEditionInviteName =
    loginStatus.data?.state === "logged in"
      ? loginStatus.data.username
      : "there";
  const hasPersonalEditionEligibleWords =
    (personalEditionVocabulary.data?.pages.flatMap((page) => page.results) ?? [])
      .length >= 30;
  const addedLanguageSpacesStorageKey =
    getAddedLanguageSpacesStorageKey(currentUserId);
  const [addedLanguageSpaces] = useLocalStorage<string[]>(
    addedLanguageSpacesStorageKey,
    [],
  );
  const [manualChecklistState, setManualChecklistState] = useLocalStorage<
    Record<string, boolean>
  >(
    currentUserId === null
      ? OVERVIEW_CHECKLIST_STORAGE_KEY
      : `${OVERVIEW_CHECKLIST_STORAGE_KEY}:${currentUserId}`,
    {},
  );
  const [titleTranslationCache, setTitleTranslationCache] = useLocalStorage<
    Record<string, string>
  >(
    currentUserId === null
      ? OVERVIEW_TITLE_TRANSLATION_CACHE_KEY
      : `${OVERVIEW_TITLE_TRANSLATION_CACHE_KEY}:${currentUserId}`,
    {},
  );
  const [resolvedTitleCache, setResolvedTitleCache] =
    useLocalStorage<ResolvedGeneratedTextTitleCache>(
      getResolvedGeneratedTextTitleCacheKey(currentUserId),
      {},
    );
  const [openingDailyLanguageId, setOpeningDailyLanguageId] = useState<
    string | null
  >(null);
  const [backgroundDailyTextsByLanguage, setBackgroundDailyTextsByLanguage] =
    useState<Record<string, GeneratedText>>({});
  const [featuredSubtitle, setFeaturedSubtitle] = useState("");
  const [settingsMenuOpen, setSettingsMenuOpen] = useState(false);
  const [
    personalEditionInvitationMinimized,
    setPersonalEditionInvitationMinimized,
  ] = useState(false);
  const backgroundDailyRequestKeysRef = useRef<Record<string, boolean>>({});
  const featuredSubtitleRequestKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const scrollContainer = document.querySelector<HTMLElement>(
      "[data-app-content-scroll]",
    );
    const mobileTopBarHeight = 64;
    const updateInvitationState = () => {
      const scrollTop = scrollContainer?.scrollTop ?? window.scrollY;
      const invitationCard = Array.from(
        document.querySelectorAll<HTMLElement>(
          "[data-personal-edition-invitation-card]",
        ),
      ).find((card) => card.getBoundingClientRect().height > 0);
      setPersonalEditionInvitationMinimized((isMinimized) => {
        if (
          !isMinimized &&
          invitationCard &&
          invitationCard.getBoundingClientRect().bottom <= mobileTopBarHeight
        )
          return true;
        if (scrollTop < 56) return false;
        return isMinimized;
      });
    };

    updateInvitationState();
    scrollContainer?.addEventListener("scroll", updateInvitationState, {
      passive: true,
    });
    window.addEventListener("scroll", updateInvitationState, { passive: true });
    return () => {
      scrollContainer?.removeEventListener("scroll", updateInvitationState);
      window.removeEventListener("scroll", updateInvitationState);
    };
  }, []);

  const serverAddedLanguageSpaces = Array.isArray(
    userSettings.data?.added_language_spaces,
  )
    ? userSettings.data.added_language_spaces
    : [];
  const hasPersistedLocalAddedLanguageSpaces =
    typeof window !== "undefined" &&
    window.localStorage.getItem(addedLanguageSpacesStorageKey) !== null;
  const hasPersistedAddedLanguageSpaces =
    serverAddedLanguageSpaces.length > 0 ||
    hasPersistedLocalAddedLanguageSpaces;
  const effectiveAddedLanguageSpaces =
    serverAddedLanguageSpaces.length > 0
      ? serverAddedLanguageSpaces
      : addedLanguageSpaces;
  const { effectiveLanguageSpaceSet } = deriveEffectiveLanguageSpaceScope({
    addedLanguageSpaces: effectiveAddedLanguageSpaces,
    enabledLanguageSpaceIds: ENABLED_LANGUAGE_SPACE_IDS,
    hasPersistedAddedLanguageSpaces,
    fallbackLanguageSpaceId:
      languageSpace === "ALL" ? "ENGLISH" : languageSpace,
  });
  const languageOptions = TEXT_LANGUAGES.filter((candidate) =>
    effectiveLanguageSpaceSet.has(candidate.id),
  );
  const visibleLanguageOptions =
    languageOptions.length > 0
      ? languageOptions
      : TEXT_LANGUAGES.filter((candidate) =>
          ENABLED_LANGUAGE_SPACE_IDS.includes(
            candidate.id as (typeof ENABLED_LANGUAGE_SPACE_IDS)[number],
          ),
        ).slice(0, 1);
  const rabbitHoleWeekQueries = useQueries({
    queries: visibleLanguageOptions.map((languageOption) => ({
      queryKey: ["rabbit-hole-current", languageOption.id],
      queryFn: async (): Promise<RabbitHoleWeek> => {
        const res = await fetch(
          `/api/reeprise/rabbit-hole-weeks/current/?language=${encodeURIComponent(languageOption.id)}`,
          {
            credentials: "include",
          },
        );
        if (!res.ok) {
          throw new Error("could not fetch rabbit hole week");
        }
        return res.json();
      },
      staleTime: 30_000,
    })),
  });
  const allTexts =
    generatedTextList.data?.pages.flatMap((page) => page.results) ?? [];
  const latestPersonalEdition = allTexts.find(isStoredPersonalEdition);
  const todayIso = getLocalIsoDate(new Date());
  const rabbitHoleCards = visibleLanguageOptions
    .map((languageOption, index) => ({
      languageOption,
      week: rabbitHoleWeekQueries[index]?.data,
      isLoading: rabbitHoleWeekQueries[index]?.isLoading ?? false,
    }))
    .sort((a, b) => {
      const tierA = getCardSortTier(a.week, todayIso);
      const tierB = getCardSortTier(b.week, todayIso);
      if (tierA !== tierB) return tierA - tierB;
      const readA = a.week?.read_discoveries ?? 0;
      const readB = b.week?.read_discoveries ?? 0;
      if (readA !== readB) return readB - readA;
      return 0;
    });
  const rabbitHoleWeeksReady = rabbitHoleWeekQueries.every(
    (query) => !query.isLoading,
  );
  useEffect(() => {
    // The daily drafts are useful background work, but creating several of
    // them during first paint competes with the overview cards on mobile.
    if (!MVP_CAPABILITIES.dailyNudgesEnabled) {
      return;
    }
    if (!generatedTextList.isSuccess || !rabbitHoleWeeksReady) {
      return;
    }

    const todayRequestKeys: string[] = [];

    for (const languageOption of visibleLanguageOptions) {
      const existingDailyText = findTodayDailyText(
        allTexts,
        languageOption.id,
        todayIso,
      );
      if (existingDailyText) {
        continue;
      }

      const requestKey = `${todayIso}:${languageOption.id}`;
      todayRequestKeys.push(requestKey);

      if (backgroundDailyRequestKeysRef.current[requestKey]) {
        continue;
      }

      backgroundDailyRequestKeysRef.current[requestKey] = true;

      void dailyTextCreate
        .mutateAsync({
          language: languageOption.id,
        })
        .then((created) => {
          setBackgroundDailyTextsByLanguage((prev) => ({
            ...prev,
            [languageOption.id]: created,
          }));
        })
        .catch(() => {
          delete backgroundDailyRequestKeysRef.current[requestKey];
        });
    }

    for (const existingKey of Object.keys(
      backgroundDailyRequestKeysRef.current,
    )) {
      if (!todayRequestKeys.includes(existingKey)) {
        delete backgroundDailyRequestKeysRef.current[existingKey];
      }
    }
  }, [
    allTexts,
    dailyTextCreate,
    generatedTextList.isSuccess,
    rabbitHoleWeeksReady,
    todayIso,
    visibleLanguageOptions,
  ]);

  const overviewTasks = visibleLanguageOptions.flatMap((languageOption) =>
    buildOverviewTasks({
      languageOption,
      allTexts,
      backgroundDailyText: backgroundDailyTextsByLanguage[languageOption.id],
      todayIso,
      translate,
      manualChecklistState,
      setManualChecklistState,
      setLanguageSpace,
      navigate,
      queryClient,
      dailyTextCreate,
      openingDailyLanguageId,
      setOpeningDailyLanguageId,
    }),
  );
  const recommendedTasks = overviewTasks.filter(
    (task) => task.section === "recommended",
  );
  const continueTasks = overviewTasks.filter(
    (task) => task.section === "continue",
  );
  const dailyTexts = recommendedTasks
    .map((task) => task.generatedText)
    .filter((text): text is GeneratedText => Boolean(text));
  const dailyTitleEntries = dailyTexts
    .map((text) => {
      const title = resolveOverviewDailyDisplayTitle(text)?.trim() || "";
      return title
        ? {
            textId: text.id,
            sourceLanguage: text.language,
            title,
          }
        : null;
    })
    .filter(
      (
        entry,
      ): entry is {
        textId: number;
        sourceLanguage: string;
        title: string;
      } => Boolean(entry),
    );
  const rabbitHoleTitleEntries = rabbitHoleCards
    .flatMap(
      ({ week }) =>
        week?.discoveries?.map((discovery) => {
          const title = (discovery.topic || "").trim();
          return discovery.id &&
            title &&
            !title.toLowerCase().startsWith("rabbit hole")
            ? {
                textId: discovery.id,
                sourceLanguage: discovery.language,
                title,
              }
            : null;
        }) ?? [],
    )
    .filter(
      (
        entry,
      ): entry is {
        textId: number;
        sourceLanguage: string;
        title: string;
      } => Boolean(entry),
    );
  const resolvedTitleEntries = [
    ...dailyTitleEntries,
    ...rabbitHoleTitleEntries,
  ];
  const featuredDailyText = selectFeaturedOverviewDailyText({
    allTexts,
    dailyTexts,
    languageOrder: visibleLanguageOptions.map((option) => option.id),
  });
  const featuredTask =
    recommendedTasks.find(
      (task) => task.generatedText?.id === featuredDailyText?.id,
    ) ??
    recommendedTasks[0] ??
    null;
  const remainingRecommendedTasks = featuredTask
    ? recommendedTasks.filter((task) => task.id !== featuredTask.id)
    : recommendedTasks;
  const featuredTitle = resolveOverviewDailyDisplayTitle(
    featuredTask?.generatedText,
  );
  const motherTongue = userSettings.data?.mother_tongue ?? "GERMAN";
  const featuredTextId = featuredTask?.generatedText?.id;
  const featuredSourceLanguage = featuredTask?.generatedText?.language;
  const featuredSourceTitleContext =
    featuredTask?.generatedText?.image_import_metadata?.original_title?.trim() ||
    featuredTask?.generatedText?.image_import_metadata?.feed_headline_title?.trim() ||
    "";
  const featuredTranslationRequestKey =
    typeof featuredTextId === "number" &&
    featuredTitle?.trim() &&
    featuredSourceLanguage
      ? `${featuredTextId}:${featuredTitle.trim()}:${motherTongue}:${featuredSourceLanguage}:${featuredSourceTitleContext}`
      : null;
  const featuredCachedSubtitle = featuredTranslationRequestKey
    ? titleTranslationCache[featuredTranslationRequestKey]?.trim() || ""
    : "";

  useEffect(() => {
    if (resolvedTitleEntries.length === 0) {
      return;
    }

    let hasChanged = false;
    const nextCache: ResolvedGeneratedTextTitleCache = {
      ...resolvedTitleCache,
    };
    for (const entry of resolvedTitleEntries) {
      if (nextCache[String(entry.textId)] === entry.title) {
        continue;
      }
      nextCache[String(entry.textId)] = entry.title;
      hasChanged = true;
    }

    if (!hasChanged) {
      return;
    }

    setResolvedTitleCache(nextCache);
  }, [resolvedTitleEntries, resolvedTitleCache, setResolvedTitleCache]);

  useEffect(() => {
    if (
      typeof featuredTextId !== "number" ||
      !featuredSourceLanguage ||
      !featuredTitle?.trim()
    ) {
      featuredSubtitleRequestKeyRef.current = null;
      setFeaturedSubtitle("");
      return;
    }

    const title = featuredTitle.trim();
    if (motherTongue === featuredSourceLanguage) {
      featuredSubtitleRequestKeyRef.current = null;
      setFeaturedSubtitle(title);
      return;
    }

    const requestKey = `${featuredTextId}:${title}:${motherTongue}:${featuredSourceLanguage}:${featuredSourceTitleContext}`;
    const cachedTranslation = titleTranslationCache[requestKey]?.trim();
    if (cachedTranslation) {
      featuredSubtitleRequestKeyRef.current = requestKey;
      setFeaturedSubtitle(cachedTranslation);
      return;
    }
    if (featuredSubtitleRequestKeyRef.current === requestKey) {
      return;
    }

    featuredSubtitleRequestKeyRef.current = requestKey;
    setFeaturedSubtitle("");

    const abortController = new AbortController();
    void fetch("/api/reeprise/translate/", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRFTOKEN": getCsrfCookie(),
      },
      credentials: "include",
      signal: abortController.signal,
      body: JSON.stringify(
        buildGeneratedTextTitleTranslationRequest({
          text: title,
          learningLang: motherTongue,
          sourceLang: featuredSourceLanguage,
          sourceTitleContext: featuredSourceTitleContext,
        }),
      ),
    })
      .then(async (response) => {
        if (!response.ok) {
          return;
        }
        const data = (await response.json()) as { translation?: string };
        const translatedTitle = (data.translation || "").trim();
        if (!translatedTitle) {
          return;
        }
        setTitleTranslationCache((prev) => ({
          ...prev,
          [requestKey]: translatedTitle,
        }));
        setFeaturedSubtitle(translatedTitle);
      })
      .catch(() => {
        // Keep the hero usable even if subtitle translation fails.
      });

    return () => {
      abortController.abort();
    };
  }, [
    featuredSourceLanguage,
    featuredSourceTitleContext,
    featuredTextId,
    featuredTitle,
    motherTongue,
    setTitleTranslationCache,
    featuredCachedSubtitle,
  ]);

  return (
    <main className="min-h-full bg-white" data-testid="app-overview-page">
      <div className="w-full max-w-none px-4 py-4 pb-24 md:px-3 md:py-8">
        {hasPersonalEditionEligibleWords || latestPersonalEdition ? (
          <div className="lg:hidden">
            <PersonalEditionsOverviewInvite
              navigate={navigate}
              username={personalEditionInviteName}
              minimized={personalEditionInvitationMinimized}
              latestEdition={latestPersonalEdition}
            />
          </div>
        ) : null}
        <div className="grid gap-10 lg:grid-cols-[minmax(0,1.1fr)_minmax(20rem,0.9fr)] lg:items-start lg:gap-12">
          <div>
            <RabbitHoleOverviewSection
              cards={rabbitHoleCards}
              navigate={navigate}
              queryClient={queryClient}
              setLanguageSpace={setLanguageSpace}
              todayIso={todayIso}
              appLanguage={language}
              allTexts={allTexts}
            />
          </div>

          <aside
            className="space-y-8"
            aria-label={translate("app_overview_recommended")}
          >
            {hasPersonalEditionEligibleWords || latestPersonalEdition ? (
              <div className="hidden lg:block">
                <PersonalEditionsOverviewInvite
                  navigate={navigate}
                  username={personalEditionInviteName}
                  desktop
                  minimized={personalEditionInvitationMinimized}
                  latestEdition={latestPersonalEdition}
                />
              </div>
            ) : null}
            <OrfDailyHeadlinesSection
              languageOptions={visibleLanguageOptions}
              appLanguage={language}
              navigate={navigate}
              languageStorageKey={`${OVERVIEW_ORF_HEADLINE_LANGUAGE_STORAGE_KEY}:${currentUserId ?? "anonymous"}`}
            />

            {featuredTask ? (
              <FeaturedOverviewTask
                task={featuredTask}
                title={featuredTitle || featuredTask.label}
                subtitle={
                  featuredSubtitle ||
                  featuredCachedSubtitle ||
                  featuredTitle ||
                  ""
                }
                wordCount={featuredTask.generatedText?.word_number ?? null}
              />
            ) : null}

            <OverviewTaskSections
              recommendedTasks={remainingRecommendedTasks}
              continueTasks={continueTasks}
              compact
              showRecommendedHeading={false}
            />
          </aside>
        </div>
        <div className="mt-12 border-t border-slate-200 pt-5">
          <button
            type="button"
            data-testid="overview-language-space-settings-link"
            onClick={() => setSettingsMenuOpen(true)}
            className="inline-flex min-h-11 items-center gap-2 text-sm font-medium text-slate-600 transition hover:text-slate-950 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2"
          >
            <span>{translate("overview_language_space_settings")}</span>
            <ChevronRightIcon className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      </div>
      {settingsMenuOpen ? (
        <OverviewSettingsMenu
          username={
            loginStatus.data?.state === "logged in"
              ? loginStatus.data.username
              : ""
          }
          spaceCodes={visibleLanguageOptions.map(
            (languageOption) => languageOption.short,
          )}
          onClose={() => setSettingsMenuOpen(false)}
          onLogout={() => login.logoutMutation.mutate()}
          translate={translate}
        />
      ) : null}
    </main>
  );
}

function OverviewSettingsMenu({
  username,
  spaceCodes,
  onClose,
  onLogout,
  translate,
}: {
  username: string;
  spaceCodes: string[];
  onClose: () => void;
  onLogout: () => void;
  translate: (key: string) => string;
}) {
  const menuRows = [
    {
      icon: <LanguageIcon className="h-6 w-6" aria-hidden="true" />,
      label: translate("overview_settings_spaces"),
      detail: spaceCodes.join(" · "),
      to: "/app/space_settings" as const,
    },
    {
      icon: <UserIcon className="h-6 w-6" aria-hidden="true" />,
      label: translate("overview_settings_profile"),
      to: "/app/profile" as const,
    },
    {
      icon: <CreditCardIcon className="h-6 w-6" aria-hidden="true" />,
      label: translate("overview_settings_account"),
      to: "/app/account" as const,
    },
    {
      icon: <QuestionMarkCircleIcon className="h-6 w-6" aria-hidden="true" />,
      label: translate("overview_settings_help"),
      to: "/app/settings" as const,
    },
  ];

  return (
    <div
      className="fixed inset-0 z-50 flex items-end bg-slate-950/20 backdrop-blur-[2px]"
      role="presentation"
    >
      <button
        type="button"
        aria-label={translate("overview_settings_close")}
        className="absolute inset-0 cursor-default"
        onClick={onClose}
      />
      <section
        role="dialog"
        aria-modal="true"
        aria-label={translate("overview_settings_menu_label")}
        className="relative w-full rounded-t-[2rem] border border-b-0 border-slate-200 bg-white px-6 pb-[calc(1.25rem+env(safe-area-inset-bottom))] pt-3 sm:mx-auto sm:max-w-xl"
      >
        <div
          className="mx-auto h-1 w-10 rounded-full bg-slate-200"
          aria-hidden="true"
        />
        <header className="mt-5 flex items-center gap-4 border-b border-slate-200 pb-5">
          <div
            className="grid h-14 w-14 place-items-center rounded-full bg-[#f3f0eb] font-serif text-2xl text-slate-800"
            aria-hidden="true"
          >
            {username.slice(0, 1).toUpperCase() || "U"}
          </div>
          <div className="min-w-0">
            <p className="font-serif text-xl leading-none text-slate-950">
              {username || "Uxhulu"}
            </p>
            <p className="mt-1 text-sm text-slate-500">
              {username ? `@${username}` : ""}
            </p>
          </div>
        </header>
        <div className="pt-4">
          <p className="px-1 text-[0.68rem] font-semibold tracking-[0.08em] text-slate-400">
            UXHULU
          </p>
          <div className="mt-2 border-y border-slate-100">
            <OverviewSettingsMenuRow {...menuRows[0]} />
          </div>
        </div>
        <div className="pt-5">
          <p className="px-1 text-[0.68rem] font-semibold tracking-[0.08em] text-slate-400">
            ACCOUNT
          </p>
          <div className="mt-2 divide-y divide-slate-100 border-y border-slate-100">
            {menuRows.slice(1, 3).map((row) => (
              <OverviewSettingsMenuRow key={row.label} {...row} />
            ))}
          </div>
        </div>
        <div className="pt-5">
          <p className="px-1 text-[0.68rem] font-semibold tracking-[0.08em] text-slate-400">
            SUPPORT
          </p>
          <div className="mt-2 border-y border-slate-100">
            <OverviewSettingsMenuRow {...menuRows[3]} />
          </div>
        </div>
        <button
          type="button"
          onClick={onLogout}
          className="mt-3 flex min-h-12 w-full items-center gap-4 px-1 text-left text-sm font-medium text-red-500"
        >
          <ArrowRightStartOnRectangleIcon
            className="h-6 w-6"
            aria-hidden="true"
          />
          {translate("logout_button")}
        </button>
      </section>
    </div>
  );
}

function OverviewSettingsMenuRow({
  icon,
  label,
  detail,
  to,
}: {
  icon: ReactNode;
  label: string;
  detail?: string;
  to: "/app/space_settings" | "/app/profile" | "/app/account" | "/app/settings";
}) {
  return (
    <Link
      to={to}
      className="flex min-h-16 items-center gap-4 px-1 py-2 text-slate-700 transition hover:text-slate-950"
    >
      <span className="text-slate-500">{icon}</span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium">{label}</span>
        {detail ? (
          <span className="mt-0.5 block text-xs text-slate-500">{detail}</span>
        ) : null}
      </span>
      <ChevronRightIcon className="h-5 w-5 text-slate-400" aria-hidden="true" />
    </Link>
  );
}

function isStoredPersonalEdition(text: GeneratedText) {
  return (
    text.image_import_metadata?.personal_edition === true ||
    (text.text_type === "STORY" &&
      !text.is_review_practice &&
      (text.struggle_words?.length ?? 0) > 0)
  );
}

function PersonalEditionsOverviewInvite({
  navigate,
  username,
  desktop = false,
  minimized,
  latestEdition,
}: {
  navigate: ReturnType<typeof useNavigate>;
  username: string;
  desktop?: boolean;
  minimized: boolean;
  latestEdition?: GeneratedText;
}) {
  return (
    <section
      className={desktop ? "" : "mb-8"}
      data-testid={desktop ? undefined : "overview-personal-editions-section"}
    >
      {latestEdition ? (
        <button
          type="button"
          data-testid="overview-personal-editions-library"
          className="mb-4 flex w-full items-center justify-between border-b border-slate-200 pb-4 text-left"
          onClick={() =>
            void navigate({
              to: "/app/personal_editions",
              search: { editionId: latestEdition.id },
            })
          }
        >
          <span className="min-w-0">
            <span className="block font-serif text-sm text-slate-500">
              {translations.personal_editions_library_eyebrow.ENGLISH}
            </span>
            <span className="mt-1 block truncate font-serif text-xl leading-tight text-slate-950">
              {latestEdition.topic || translations.personal_editions_library_untitled.ENGLISH}
            </span>
          </span>
          <span className="ml-4 inline-flex shrink-0 items-center gap-2 text-sm text-slate-700">
            {translations.personal_editions_library_action.ENGLISH}
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
          </span>
        </button>
      ) : null}
      <article
        data-personal-edition-invitation-card
        className="personal-edition-invitation-card relative grid min-h-[12rem] cursor-pointer grid-cols-[minmax(0,1fr)_7.5rem] items-end gap-5 overflow-hidden rounded-lg bg-[#f4f5f8] px-5 py-5 sm:grid-cols-[minmax(0,18rem)_9rem] sm:px-6 sm:py-6"
        role="button"
        tabIndex={0}
        onClick={() => void navigate({ to: "/app/personal_editions", search: { editionId: undefined } })}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            void navigate({ to: "/app/personal_editions", search: { editionId: undefined } });
          }
        }}
      >
        <div>
          <p
            className="mb-2 font-serif text-lg italic leading-none text-slate-700"
            data-testid="overview-personal-editions-greeting"
          >
            Hi, {username}
          </p>
          <h2 className="max-w-[16rem] font-serif text-[1.55rem] leading-[1.08] text-slate-950 sm:text-[1.7rem]">
            {translations.personal_editions_invite_title.ENGLISH}
          </h2>
          <p className="mt-2.5 max-w-[16rem] font-serif text-[15px] leading-6 text-slate-600">
            {translations.personal_editions_invite_body.ENGLISH}
          </p>
        </div>
        <div className="pointer-events-none self-end pb-1" aria-hidden="true">
          <RabbitHoleEditionCover
            topic="Personal Edition"
            title=""
            language="ITALIAN"
            languageLabel="No. 01"
            weekStart="2026-08-31"
            weekEnd="2026-08-31"
            dayIndex={0}
            completed={0}
            total={1}
            dayLabel="Italiano"
            progressLabel="August 2026"
            verticalLabel=""
            dateRangeLabel=""
            variant="mini"
            showTopicHeader={false}
            personal
            testId="overview-personal-editions-cover"
          />
        </div>
        <div className="col-span-2 mt-1 flex items-center gap-4">
          <span
            className="inline-flex min-h-10 w-full items-center justify-between gap-3 bg-[#c1bedc] px-3.5 text-base font-medium text-[#35324a]"
            data-testid="overview-personal-editions-action"
          >
            {translations.personal_editions_invite_action.ENGLISH}
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
          </span>
        </div>
      </article>
      {minimized ? (
        <article
          data-testid="overview-personal-editions-minimized"
          className="personal-edition-invitation-minimized fixed inset-x-4 top-[4.75rem] z-40 flex min-h-14 cursor-pointer items-center gap-3 rounded-lg bg-slate-50 px-4 shadow-[0_8px_22px_rgba(15,23,42,0.09)] lg:absolute lg:left-auto lg:right-8 lg:top-4 lg:w-[22rem]"
          role="button"
          tabIndex={0}
          onClick={() => void navigate({ to: "/app/personal_editions", search: { editionId: undefined } })}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              void navigate({ to: "/app/personal_editions", search: { editionId: undefined } });
            }
          }}
        >
          <span className="min-w-0 flex-1">
            <span className="block truncate font-serif text-[0.98rem] leading-tight text-slate-900">
              Your book
            </span>
          </span>
          <span
            className="inline-flex min-h-9 shrink-0 items-center gap-2 bg-[#c1bedc] px-3 text-[12px] font-medium text-[#35324a]"
            data-testid="overview-personal-editions-action"
          >
            {translations.personal_editions_invite_action.ENGLISH}
            <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
          </span>
        </article>
      ) : null}
    </section>
  );
}

function OrfDailyHeadlinesSection({
  languageOptions,
  appLanguage,
  navigate,
  languageStorageKey,
}: {
  languageOptions: (typeof TEXT_LANGUAGES)[number][];
  appLanguage: string;
  navigate: ReturnType<typeof useNavigate>;
  languageStorageKey: string;
}) {
  const { translate } = useTranslation();
  const headlineQueries = useOrfDailyHeadlineQueries(
    languageOptions.map((languageOption) => languageOption.id),
  );
  const createReadingText = useOrfHeadlineReadingTextCreate();
  const [selectedLanguagesByHeadlineId, setSelectedLanguagesByHeadlineId] =
    useLocalStorage<Record<string, string>>(languageStorageKey, {});
  const languageHeadlineGroups = languageOptions.map(
    (languageOption, index) => ({
      languageOption,
      headlines: headlineQueries[index]?.data?.headlines ?? [],
      snapshotDate: headlineQueries[index]?.data?.snapshot_date ?? "",
    }),
  );

  if (!languageHeadlineGroups.some((group) => group.headlines.length > 0)) {
    return null;
  }

  const openHeadline = async (
    headline: OrfDailyHeadline,
    selectedLanguage: string,
    sourceLanguage: string,
  ) => {
    try {
      const generatedTextId =
        selectedLanguage === sourceLanguage && headline.generated_text_id
          ? headline.generated_text_id
          : (
              await createReadingText.mutateAsync({
                headlineId: headline.id,
                language: selectedLanguage,
              })
            ).id;
      await navigate({
        to: "/app/reeprise/$generatedTextId",
        params: { generatedTextId: `${generatedTextId}` },
        search: {
          chapter: undefined,
          focusWord: undefined,
          focusSentence: undefined,
          focusToken: undefined,
          jumpTo: undefined,
        },
      });
    } catch (error) {
      notifySnackbar({
        message:
          error instanceof Error && error.message === "AUTH_REQUIRED"
            ? translate("auth_session_expired")
            : translate("orf_headlines_create_error"),
      });
    }
  };

  const recommendations = languageHeadlineGroups.flatMap(
    ({ languageOption, headlines, snapshotDate }) =>
      headlines
        .slice(0, 1)
        .map((headline) => ({ languageOption, headline, snapshotDate })),
  );
  const sectionSnapshotDate =
    recommendations.find(({ snapshotDate }) => snapshotDate)?.snapshotDate ??
    "";
  const sectionDateLabel = sectionSnapshotDate
    ? new Intl.DateTimeFormat(appLanguage === "GERMAN" ? "de-DE" : "en-US", {
        day: "numeric",
        month: "long",
        year: "numeric",
      }).format(new Date(`${sectionSnapshotDate}T00:00:00`))
    : "";

  return (
    <section
      className="border-t border-slate-200 pt-5"
      data-testid="overview-orf-headlines-section"
    >
      <h2 className="font-serif text-[1.75rem] leading-tight text-slate-950">
        {translate("overview_orf_heading")}
      </h2>
      {sectionDateLabel ? (
        <p className="mt-1 text-sm text-slate-500">{sectionDateLabel}</p>
      ) : null}
      <div className="mt-3 divide-y divide-slate-200">
        {recommendations.map(({ languageOption, headline, snapshotDate }) => {
          const generatedVersions = (headline.generated_versions ?? [])
            .filter((version) =>
              languageOptions.some((option) => option.id === version.language),
            )
            .sort(
              (left, right) =>
                languageOptions.findIndex(
                  (option) => option.id === left.language,
                ) -
                languageOptions.findIndex(
                  (option) => option.id === right.language,
                ),
            );
          const generatedLanguageIds = new Set(
            generatedVersions.map((version) => version.language),
          );
          const savedSelectedLanguage =
            selectedLanguagesByHeadlineId[`${headline.id}`];
          const firstMissingLanguage = languageOptions.find(
            (option) => !generatedLanguageIds.has(option.id),
          )?.id;
          const selectedLanguage =
            savedSelectedLanguage ?? firstMissingLanguage ?? languageOption.id;
          const hasSelectedLanguage = languageOptions.some(
            (option) => option.id === selectedLanguage,
          );
          const effectiveSelectedLanguage = hasSelectedLanguage
            ? selectedLanguage
            : languageOption.id;
          const selectedLanguageOption = languageOptions.find(
            (option) => option.id === effectiveSelectedLanguage,
          );
          const isCreating =
            createReadingText.isPending &&
            createReadingText.variables?.headlineId === headline.id &&
            createReadingText.variables?.language === effectiveSelectedLanguage;
          return (
            <div
              key={headline.id}
              className="w-full py-3"
              data-testid={`overview-orf-headline-${languageOption.id}-${headline.position}`}
            >
              <div className="flex min-h-[4.5rem] items-center gap-4">
                <span className="relative grid h-9 w-9 shrink-0 place-items-center rounded-[3px] border border-slate-300 bg-slate-50 text-xs font-medium text-slate-700">
                  <select
                    aria-label={translate("orf_headlines_language_select")}
                    value={effectiveSelectedLanguage}
                    disabled={isCreating}
                    onChange={(event) =>
                      setSelectedLanguagesByHeadlineId((current) => ({
                        ...current,
                        [headline.id]: event.target.value,
                      }))
                    }
                    className="absolute inset-0 z-10 h-full w-full cursor-pointer appearance-none opacity-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 disabled:cursor-wait"
                    data-testid={`overview-orf-headline-language-${headline.id}`}
                  >
                    {languageOptions.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.short}
                      </option>
                    ))}
                  </select>
                  <span className="pointer-events-none flex items-center gap-0.5">
                    {selectedLanguageOption?.short ?? effectiveSelectedLanguage}
                    <ChevronDownIcon
                      className="h-3 w-3 text-slate-500"
                      aria-hidden="true"
                    />
                  </span>
                </span>
                <button
                  type="button"
                  disabled={isCreating}
                  onClick={() =>
                    void openHeadline(
                      headline,
                      effectiveSelectedLanguage,
                      languageOption.id,
                    )
                  }
                  className="flex min-h-[4.5rem] min-w-0 flex-1 items-center gap-4 text-left transition hover:bg-slate-50 disabled:cursor-wait"
                  data-testid={`overview-orf-headline-action-${headline.id}`}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block font-serif text-[1rem] leading-tight text-slate-950">
                      {headline.title}
                    </span>
                    <span className="mt-1 block text-xs text-slate-500">
                      ORF ·{" "}
                      {formatRabbitHoleEditionDateRange(
                        snapshotDate,
                        snapshotDate,
                        appLanguage,
                      )}
                    </span>
                  </span>
                  <ArrowRightIcon
                    className="h-5 w-5 shrink-0 text-slate-900"
                    aria-hidden="true"
                  />
                </button>
              </div>
              {generatedVersions.length > 0 ? (
                <div
                  className="ml-[3.25rem] mt-1 border-l border-slate-200 pl-4"
                  data-testid={`overview-orf-headline-versions-${headline.id}`}
                >
                  <p className="mb-1 text-xs text-slate-500">
                    {translate("orf_headlines_generated_versions")}
                  </p>
                  <div className="divide-y divide-slate-100">
                    {generatedVersions.map((version) => {
                      const versionLanguage = languageOptions.find(
                        (option) => option.id === version.language,
                      );
                      const languageLabel = versionLanguage
                        ? translate(versionLanguage.prettyTranslationKey)
                        : version.language;
                      return (
                        <button
                          key={version.generated_text_id}
                          type="button"
                          onClick={() =>
                            void navigate({
                              to: "/app/reeprise/$generatedTextId",
                              params: {
                                generatedTextId: `${version.generated_text_id}`,
                              },
                              search: {
                                chapter: undefined,
                                focusWord: undefined,
                                focusSentence: undefined,
                                focusToken: undefined,
                                jumpTo: undefined,
                              },
                            })
                          }
                          className="flex min-h-11 w-full items-center gap-3 py-2 text-left hover:bg-slate-50"
                          data-testid={`overview-orf-headline-version-${headline.id}-${version.language}`}
                          aria-label={translate("orf_headlines_open_version", {
                            language: languageLabel,
                          })}
                        >
                          <span className="w-6 shrink-0 text-xs font-medium text-slate-500">
                            {versionLanguage?.short ?? version.language}
                          </span>
                          <span className="min-w-0 flex-1 font-serif text-sm leading-tight text-slate-800">
                            {version.title}
                          </span>
                          <ArrowRightIcon
                            className="h-4 w-4 shrink-0 text-slate-500"
                            aria-hidden="true"
                          />
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </section>
  );
}

export function RabbitHoleOverviewSection({
  cards,
  navigate,
  queryClient,
  setLanguageSpace,
  todayIso,
  appLanguage,
  allTexts,
  transparentCardSurface = false,
}: {
  cards: Array<{
    languageOption: (typeof TEXT_LANGUAGES)[number];
    week: RabbitHoleWeek | undefined;
    isLoading: boolean;
  }>;
  navigate: ReturnType<typeof useNavigate>;
  queryClient: ReturnType<typeof useQueryClient>;
  setLanguageSpace: (
    value: "ENGLISH" | "FRENCH" | "GERMAN" | "ITALIAN",
  ) => void;
  todayIso: string;
  appLanguage: string;
  allTexts: GeneratedText[];
  transparentCardSurface?: boolean;
}) {
  const { translate } = useTranslation();
  const createWeek = useRabbitHoleCreateWeek();
  const refreshTopicSuggestion = useRabbitHoleRefreshTopicSuggestion();
  const [expandedLanguages, setExpandedLanguages] = useState<
    Record<string, boolean>
  >({});
  const [topicSuggestionOverrides, setTopicSuggestionOverrides] = useState<
    Record<string, RabbitHoleTopicSuggestion>
  >({});
  const [refreshingSuggestionLanguages, setRefreshingSuggestionLanguages] =
    useState<Record<string, boolean>>({});
  const [selectedLanguageId, setSelectedLanguageId] = useState<string | null>(
    null,
  );
  const selectedCard =
    cards.find(
      ({ languageOption }) => languageOption.id === selectedLanguageId,
    ) ?? cards[0];
  const inactiveLanguages = cards
    .filter(({ week }) => !isRabbitHoleCardActive(week))
    .map(({ languageOption }) => languageOption.id);
  const topicSuggestionsQuery =
    useRabbitHoleTopicSuggestionsBatch(inactiveLanguages);

  const openPlanner = async (languageId: string) => {
    setLanguageSpace(languageId as "ENGLISH" | "FRENCH" | "GERMAN" | "ITALIAN");
    await navigate({
      to: "/app/rabbit_hole",
      search: { weekId: undefined, summary: undefined },
    });
  };

  const toggleExpanded = (languageId: string) => {
    setExpandedLanguages((prev) => ({
      ...prev,
      [languageId]: !prev[languageId],
    }));
  };

  const [pickingTopics, setPickingTopics] = useState<Record<string, string>>(
    {},
  );
  const [pendingClarificationTopics, setPendingClarificationTopics] = useState<
    Record<string, RabbitHoleTopicRefinement>
  >({});
  const [checkingClarificationLanguages, setCheckingClarificationLanguages] =
    useState<Record<string, boolean>>({});
  useEffect(() => {
    const querySuggestions = topicSuggestionsQuery.data ?? {};
    const mergedSuggestions: Record<string, RabbitHoleTopicSuggestion> = {};
    Object.entries(querySuggestions).forEach(([language, suggestions]) => {
      if (suggestions?.[0]) {
        mergedSuggestions[language] = suggestions[0];
      }
    });
    setTopicSuggestionOverrides((previous) => ({
      ...mergedSuggestions,
      ...previous,
    }));
  }, [topicSuggestionsQuery.data]);

  const createWeekForTopic = async (languageId: string, topic: string) => {
    const createdWeek = await createWeek.mutateAsync({
      topic,
      language: languageId,
    });
    setLanguageSpace(languageId as "ENGLISH" | "FRENCH" | "GERMAN" | "ITALIAN");
    await navigate({
      to: "/app/rabbit_hole",
      search: { weekId: createdWeek.id, summary: undefined },
    });
  };

  const handleCustomTopicSubmit = async (languageId: string, topic: string) => {
    const trimmed = topic.trim();
    if (!trimmed) return;

    const refinement = getRabbitHoleTopicRefinement(trimmed);
    if (refinement) {
      setPendingClarificationTopics((previous) => ({
        ...previous,
        [languageId]: refinement,
      }));
      return;
    }

    setCheckingClarificationLanguages((previous) => ({
      ...previous,
      [languageId]: true,
    }));

    try {
      const wikipediaRefinement = await fetchWikipediaRefinement(trimmed);
      if (wikipediaRefinement) {
        setPendingClarificationTopics((previous) => ({
          ...previous,
          [languageId]: wikipediaRefinement,
        }));
        return;
      }
      await createWeekForTopic(languageId, trimmed);
    } finally {
      setCheckingClarificationLanguages((previous) => {
        const next = { ...previous };
        delete next[languageId];
        return next;
      });
    }
  };

  const handleClarificationChoice = async (
    languageId: string,
    topic: string,
  ) => {
    setPendingClarificationTopics((previous) => {
      const next = { ...previous };
      delete next[languageId];
      return next;
    });
    await createWeekForTopic(languageId, topic);
  };

  const handleKeepBroadTopic = async (languageId: string, topic: string) => {
    setPendingClarificationTopics((previous) => {
      const next = { ...previous };
      delete next[languageId];
      return next;
    });
    await createWeekForTopic(languageId, topic);
  };

  return (
    <section data-testid="overview-rabbit-hole-section">
      {cards.length > 0 ? (
        <>
          <div
            className="grid min-w-0 grid-cols-4 gap-2 sm:gap-3"
            data-testid="overview-rabbit-hole-grid"
          >
            {cards.map(({ languageOption, week }) => {
              const discovery = findRabbitHoleDiscoveryForDate(week, todayIso);
              const storedEditionTopic = week?.topic ?? "";
              const hasActiveEdition = isRabbitHoleCardActive(week);
              const hasTodaysDiscovery = hasActiveEdition && Boolean(discovery);
              const title = hasTodaysDiscovery
                ? getRabbitHoleEditionTitle(discovery?.topic).title ||
                  storedEditionTopic
                : storedEditionTopic ||
                  topicSuggestionOverrides[languageOption.id]?.topic ||
                  "";
              const previewLabel = hasTodaysDiscovery
                ? translate("rabbit_hole_todays_discovery")
                : hasActiveEdition || storedEditionTopic
                  ? translate("rabbit_hole_weekly_review")
                  : translate("rabbit_hole_topic_suggestion");
              const nativeLanguageLabel =
                OVERVIEW_NATIVE_LANGUAGE_LABELS[languageOption.id] ||
                translate(languageOption.prettyTranslationKey);
              const usesCondensedPreviewTitle =
                estimateEditionTextWidth(title) > 18;
              const isSelected =
                languageOption.id === selectedCard?.languageOption.id;
              return (
                <button
                  key={languageOption.id}
                  type="button"
                  onClick={() => setSelectedLanguageId(languageOption.id)}
                  aria-pressed={isSelected}
                  aria-label={`${nativeLanguageLabel}: ${previewLabel}. ${title}`}
                  className={`relative min-w-0 border-b-4 pb-2 text-left ${isSelected ? "border-slate-950" : "border-transparent"}`}
                  data-testid={`overview-rabbit-hole-preview-${languageOption.id.toLowerCase()}`}
                >
                  <RabbitHoleEditionCover
                    topic={
                      week?.topic ?? translate("rabbit_hole_next_edition_title")
                    }
                    title=""
                    language={languageOption.id}
                    languageLabel={
                      OVERVIEW_NATIVE_LANGUAGE_LABELS[languageOption.id] ||
                      translate(languageOption.prettyTranslationKey)
                    }
                    weekStart={week?.week_start ?? todayIso}
                    weekEnd={week?.week_end ?? todayIso}
                    dayIndex={discovery?.rabbit_hole_day_index ?? 0}
                    completed={week?.read_discoveries ?? 0}
                    total={week?.total_discoveries ?? 6}
                    dayLabel={translate("rabbit_hole_edition_day", {
                      day: (discovery?.rabbit_hole_day_index ?? 0) + 1,
                    })}
                    progressLabel={translate("rabbit_hole_progress", {
                      completed: week?.read_discoveries ?? 0,
                      total: week?.total_discoveries ?? 6,
                    })}
                    verticalLabel={translate(
                      "rabbit_hole_edition_vertical_label",
                    )}
                    dateRangeLabel={formatRabbitHoleEditionDateRange(
                      week?.week_start ?? todayIso,
                      week?.week_end ?? todayIso,
                      appLanguage,
                    )}
                    variant="mini"
                    previewOnly
                    testId={`overview-rabbit-hole-preview-cover-${languageOption.id.toLowerCase()}`}
                  />
                  <span
                    className="pointer-events-none absolute left-6 right-2 top-3 overflow-hidden text-[clamp(0.48rem,1.7vw,0.64rem)] font-medium leading-none text-slate-500"
                    data-testid={`overview-rabbit-hole-preview-label-${languageOption.id.toLowerCase()}`}
                  >
                    {nativeLanguageLabel}
                  </span>
                  {title ? (
                    <span
                      className={`pointer-events-none absolute left-6 right-3 top-1/2 -translate-y-1/2 font-serif leading-[1.02] tracking-[-0.035em] text-balance text-slate-950 ${usesCondensedPreviewTitle ? "text-[clamp(0.58rem,2.2vw,0.98rem)]" : "text-[clamp(0.78rem,2.8vw,1.15rem)]"}`}
                      data-testid={`overview-rabbit-hole-preview-title-${languageOption.id.toLowerCase()}`}
                    >
                      {title}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
          <div className="mt-3 border-t border-slate-200 pt-3">
            <div
              className="grid min-w-0 gap-3"
              data-testid="overview-rabbit-hole-selected"
            >
              {[selectedCard]
                .filter(Boolean)
                .map(({ languageOption, week, isLoading }) => {
                  const isWeekLoading = isLoading && week === undefined;
                  const hasWeek = week !== undefined && week.status !== "none";
                  const suggestion =
                    topicSuggestionOverrides[languageOption.id] ?? null;
                  const suggestionLocallyRefreshing =
                    refreshingSuggestionLanguages[languageOption.id] === true;
                  const suggestionLoading =
                    !hasWeek &&
                    !isWeekLoading &&
                    topicSuggestionsQuery.isLoading &&
                    !suggestion;
                  const suggestionUnavailable =
                    !hasWeek &&
                    !isWeekLoading &&
                    topicSuggestionsQuery.isSuccess &&
                    !suggestion;
                  const suggestionRefreshing =
                    refreshTopicSuggestion.isPending &&
                    refreshTopicSuggestion.variables?.language ===
                      languageOption.id;
                  const progressLabel =
                    hasWeek &&
                    typeof week.read_discoveries === "number" &&
                    typeof week.total_discoveries === "number"
                      ? translate("rabbit_hole_progress", {
                          completed: week.read_discoveries,
                          total: week.total_discoveries,
                        })
                      : null;
                  const todaysDiscovery = findRabbitHoleDiscoveryForDate(
                    week,
                    todayIso,
                  );
                  const recapReadDiscoveries =
                    week?.discoveries?.filter(
                      (discovery) => discovery.view_count > 0,
                    ).length ?? 0;
                  const recapProgressLabel = translate(
                    "rabbit_hole_recap_progress",
                    {
                      read: recapReadDiscoveries,
                      total: week?.total_discoveries ?? 6,
                    },
                  );
                  const effectiveTodaysDiscovery =
                    resolveEffectiveRabbitHoleDiscovery({
                      discovery: todaysDiscovery,
                      generatedTexts: allTexts,
                    });
                  const archivedWeek = week as RabbitHoleWeek | undefined;
                  const upcomingSlots =
                    hasWeek && week
                      ? buildAllRabbitHoleSlots(week, todayIso, appLanguage)
                      : [];
                  const isExpanded =
                    expandedLanguages[languageOption.id] === true;
                  const pickTopic = pickingTopics[languageOption.id] ?? "";
                  const clarification =
                    pendingClarificationTopics[languageOption.id] ?? null;
                  const isCheckingClarification =
                    checkingClarificationLanguages[languageOption.id] === true;

                  return (
                    <div
                      key={`${languageOption.id}:${week?.id ?? "unset"}`}
                      onClick={async () => {
                        if (todaysDiscovery && effectiveTodaysDiscovery) {
                          void openRabbitHoleDiscovery({
                            discovery: todaysDiscovery,
                            languageSpace: languageOption.id,
                            generatedTexts: allTexts,
                            queryClient,
                            navigate,
                            setLanguageSpace,
                          });
                          return;
                        }
                        if (archivedWeek?.status === "COMPLETED") {
                          void navigate({
                            to: "/app/rabbit_hole",
                            search: {
                              weekId: archivedWeek.id,
                              summary: undefined,
                            },
                          });
                          return;
                        }
                        if (
                          isRabbitHoleCardActive(week) &&
                          typeof week.id === "number"
                        ) {
                          setLanguageSpace(
                            languageOption.id as
                              | "ENGLISH"
                              | "FRENCH"
                              | "GERMAN"
                              | "ITALIAN",
                          );
                          void navigate({
                            to: "/app/rabbit_hole",
                            search: { weekId: week.id, summary: true },
                          });
                        }
                      }}
                      className={`relative w-full min-w-0 overflow-hidden rounded-2xl p-4 text-left transition ${transparentCardSurface ? "bg-transparent" : "bg-white"} ${
                        todaysDiscovery && effectiveTodaysDiscovery
                          ? "cursor-pointer hover:bg-slate-50"
                          : archivedWeek?.status === "COMPLETED"
                            ? "cursor-pointer hover:bg-slate-50"
                            : isRabbitHoleCardActive(week)
                              ? "cursor-pointer hover:bg-slate-50"
                              : ""
                      }`}
                      data-testid={`overview-rabbit-hole-card-${languageOption.id.toLowerCase()}`}
                    >
                      {isRabbitHoleCardActive(week) ? (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            void openPlanner(languageOption.id);
                          }}
                          className="absolute right-4 top-3 z-20 rounded p-1 hover:bg-black/5"
                          data-testid={`overview-rabbit-hole-manage-${languageOption.id.toLowerCase()}`}
                          aria-label={translate("overview_rabbit_hole_manage")}
                        >
                          <EllipsisHorizontalIcon className="h-5 w-5 shrink-0 text-slate-500" />
                        </button>
                      ) : null}

                      {isRabbitHoleCardActive(week) ? (
                        <RabbitHoleEditionCover
                          topic={week.topic}
                          title={
                            todaysDiscovery
                              ? getRabbitHoleEditionTitle(todaysDiscovery.topic)
                                  .title
                              : translate("rabbit_hole_weekly_review")
                          }
                          language={week.language}
                          languageLabel={
                            OVERVIEW_NATIVE_LANGUAGE_LABELS[
                              languageOption.id
                            ] || translate(languageOption.prettyTranslationKey)
                          }
                          weekStart={week.week_start}
                          weekEnd={week.week_end}
                          dayIndex={todaysDiscovery?.rabbit_hole_day_index ?? 0}
                          completed={week.read_discoveries ?? 0}
                          total={week.total_discoveries ?? 6}
                          dayLabel={
                            todaysDiscovery
                              ? translate("rabbit_hole_edition_day", {
                                  day:
                                    (todaysDiscovery.rabbit_hole_day_index ??
                                      0) + 1,
                                })
                              : ""
                          }
                          progressLabel={
                            todaysDiscovery
                              ? progressLabel ||
                                translate("rabbit_hole_progress", {
                                  completed: 0,
                                  total: week.total_discoveries ?? 6,
                                })
                              : recapProgressLabel
                          }
                          verticalLabel={translate(
                            "rabbit_hole_edition_vertical_label",
                          )}
                          dateRangeLabel={formatRabbitHoleEditionDateRange(
                            week.week_start,
                            week.week_end,
                            appLanguage,
                          )}
                          actionLabel={
                            todaysDiscovery
                              ? (READ_TODAY_CTA_BY_LANGUAGE[
                                  languageOption.id
                                ] ?? translate("rabbit_hole_read_today"))
                              : undefined
                          }
                          readActionLabel={
                            todaysDiscovery
                              ? (CONTINUE_READING_CTA_BY_LANGUAGE[
                                  languageOption.id
                                ] ?? translate("rabbit_hole_continue_reading"))
                              : undefined
                          }
                          isRead={
                            todaysDiscovery
                              ? isRabbitHoleDiscoveryRead(
                                  effectiveTodaysDiscovery,
                                )
                              : false
                          }
                          onAction={
                            todaysDiscovery
                              ? () =>
                                  void openRabbitHoleDiscovery({
                                    discovery: todaysDiscovery,
                                    languageSpace: languageOption.id,
                                    generatedTexts: allTexts,
                                    queryClient,
                                    navigate,
                                    setLanguageSpace,
                                  })
                              : undefined
                          }
                          variant="full"
                          featured
                          testId={`overview-rabbit-hole-edition-${languageOption.id.toLowerCase()}`}
                        />
                      ) : archivedWeek?.status === "COMPLETED" ? (
                        <div className="flex flex-col">
                          <RabbitHoleEditionCover
                            topic={archivedWeek.topic}
                            title={translate("rabbit_hole_complete")}
                            language={languageOption.id}
                            languageLabel={
                              OVERVIEW_NATIVE_LANGUAGE_LABELS[
                                languageOption.id
                              ] ||
                              translate(languageOption.prettyTranslationKey)
                            }
                            weekStart={archivedWeek.week_start}
                            weekEnd={archivedWeek.week_end}
                            dayIndex={6}
                            completed={archivedWeek.read_discoveries ?? 0}
                            total={archivedWeek.total_discoveries ?? 6}
                            dayLabel={translate("rabbit_hole_this_week")}
                            progressLabel={translate("rabbit_hole_progress", {
                              completed: archivedWeek.read_discoveries ?? 0,
                              total: archivedWeek.total_discoveries ?? 6,
                            })}
                            verticalLabel={translate(
                              "rabbit_hole_edition_vertical_label",
                            )}
                            dateRangeLabel={formatRabbitHoleEditionDateRange(
                              archivedWeek.week_start,
                              archivedWeek.week_end,
                              appLanguage,
                            )}
                            unavailableLabel={translate(
                              "overview_rabbit_hole_archived_title",
                            )}
                            actionLabel={translate("rabbit_hole_open_summary")}
                            onAction={() =>
                              void navigate({
                                to: "/app/rabbit_hole",
                                search: {
                                  weekId: archivedWeek.id,
                                  summary: true,
                                },
                              })
                            }
                            variant="compact"
                            testId={`overview-rabbit-hole-archive-${languageOption.id.toLowerCase()}`}
                          />
                          <p
                            className="mt-3 text-sm text-slate-500"
                            data-testid={`overview-rabbit-hole-archive-label-${languageOption.id.toLowerCase()}`}
                          >
                            {translate("overview_rabbit_hole_archived_title")}
                          </p>
                        </div>
                      ) : createWeek.isPending &&
                        createWeek.variables?.language === languageOption.id ? (
                        <div onClick={(e) => e.stopPropagation()}>
                          <div>
                            <RabbitHoleRecommendationCover
                              topic={
                                suggestionLocallyRefreshing
                                  ? ""
                                  : (suggestion?.topic ?? "")
                              }
                              language={languageOption.id}
                              weekStart={todayIso}
                              verticalLabel={translate(
                                "rabbit_hole_edition_vertical_label",
                              )}
                              recommendationLabel={
                                OVERVIEW_NATIVE_LANGUAGE_LABELS[
                                  languageOption.id
                                ] ||
                                translate(languageOption.prettyTranslationKey)
                              }
                              variant="compact"
                              featured
                              titlePending={
                                createWeek.isPending ||
                                suggestionLocallyRefreshing ||
                                suggestionRefreshing
                              }
                              testId={`overview-rabbit-hole-recommendation-${languageOption.id.toLowerCase()}`}
                            />
                          </div>
                        </div>
                      ) : isWeekLoading ? (
                        <div className="pr-8 animate-pulse">
                          <div className="h-3 w-16 rounded bg-slate-200" />
                          <div className="mt-3 mb-3 space-y-2">
                            <div className="h-4 w-full rounded bg-slate-200" />
                            <div className="h-4 w-3/4 rounded bg-slate-200" />
                          </div>
                          <div className="h-2 w-24 rounded bg-slate-200" />
                        </div>
                      ) : (
                        <div onClick={(e) => e.stopPropagation()}>
                          <div>
                            <RabbitHoleRecommendationCover
                              topic={
                                suggestionRefreshing
                                  ? ""
                                  : (suggestion?.topic ?? "")
                              }
                              language={languageOption.id}
                              weekStart={todayIso}
                              verticalLabel={translate(
                                "rabbit_hole_edition_vertical_label",
                              )}
                              recommendationLabel={
                                OVERVIEW_NATIVE_LANGUAGE_LABELS[
                                  languageOption.id
                                ] ||
                                translate(languageOption.prettyTranslationKey)
                              }
                              variant="compact"
                              featured
                              titlePending={suggestionRefreshing}
                              testId={`overview-rabbit-hole-recommendation-${languageOption.id.toLowerCase()}`}
                            />
                          </div>

                          <div
                            className={`mt-3 lg:mx-auto lg:max-w-[32rem] ${suggestionRefreshing ? "opacity-70" : ""}`}
                          >
                            <div className="flex flex-col gap-2">
                              {suggestion?.topic ? (
                                <button
                                  type="button"
                                  onClick={() => {
                                    void createWeek
                                      .mutateAsync({
                                        topic: suggestion.topic,
                                        language: languageOption.id,
                                        topic_source: "IDEA",
                                        topic_suggestion_id: suggestion.id,
                                      })
                                      .then((createdWeek) => {
                                        setLanguageSpace(
                                          languageOption.id as
                                            | "ENGLISH"
                                            | "FRENCH"
                                            | "GERMAN"
                                            | "ITALIAN",
                                        );
                                        void navigate({
                                          to: "/app/rabbit_hole",
                                          search: {
                                            weekId: createdWeek.id,
                                            summary: undefined,
                                          },
                                        });
                                      })
                                      .catch((error: unknown) => {
                                        void queryClient.invalidateQueries({
                                          queryKey: ["rabbit-hole-current"],
                                        });
                                        notifySnackbar({
                                          message:
                                            error instanceof
                                              RabbitHoleCreateError &&
                                            error.code === "rabbit_hole_active"
                                              ? translate(
                                                  "rabbit_hole_active_edition",
                                                )
                                              : translate(
                                                  "rabbit_hole_create_error",
                                                ),
                                        });
                                      });
                                  }}
                                  className="inline-flex min-h-12 w-full cursor-pointer items-center justify-between rounded-[5px] border border-slate-950 bg-slate-950 px-5 text-left text-sm font-medium text-white transition hover:border-slate-800 hover:bg-slate-800 disabled:cursor-not-allowed disabled:border-slate-300 disabled:bg-slate-300 disabled:text-slate-500"
                                  disabled={
                                    suggestionRefreshing || suggestionLoading
                                  }
                                  data-testid={`overview-rabbit-hole-recommendation-start-${languageOption.id.toLowerCase()}`}
                                >
                                  {translate("rabbit_hole_choose_recommended")}
                                </button>
                              ) : null}
                              <button
                                type="button"
                                onClick={() => {
                                  setRefreshingSuggestionLanguages(
                                    (previous) => ({
                                      ...previous,
                                      [languageOption.id]: true,
                                    }),
                                  );
                                  void refreshTopicSuggestion
                                    .mutateAsync({
                                      language: languageOption.id,
                                    })
                                    .then((nextSuggestion) => {
                                      setTopicSuggestionOverrides(
                                        (previous) => ({
                                          ...previous,
                                          [languageOption.id]: nextSuggestion,
                                        }),
                                      );
                                    })
                                    .catch(() => {
                                      // Keep the current suggestion visible on refresh failure.
                                    })
                                    .finally(() => {
                                      setRefreshingSuggestionLanguages(
                                        (previous) => {
                                          const next = { ...previous };
                                          delete next[languageOption.id];
                                          return next;
                                        },
                                      );
                                    });
                                }}
                                disabled={
                                  suggestionRefreshing || suggestionLoading
                                }
                                className="inline-flex w-full items-center gap-2 text-sm font-medium text-slate-600 transition hover:text-slate-900 disabled:cursor-not-allowed disabled:text-slate-300"
                                data-testid={`overview-rabbit-hole-regenerate-${languageOption.id.toLowerCase()}`}
                              >
                                <ArrowPathIcon
                                  className="h-4 w-4 shrink-0"
                                  aria-hidden="true"
                                />
                                <span>
                                  {translate(
                                    "rabbit_hole_another_recommendation",
                                  )}
                                </span>
                              </button>
                            </div>
                          </div>

                          {suggestionUnavailable ? (
                            <p className="mt-4 text-sm text-slate-500">
                              {translate(
                                "overview_rabbit_hole_no_recommendation",
                              )}
                            </p>
                          ) : null}

                          <form
                            onSubmit={(e) => {
                              e.preventDefault();
                              void handleCustomTopicSubmit(
                                languageOption.id,
                                pickTopic,
                              ).catch((error: unknown) => {
                                void queryClient.invalidateQueries({
                                  queryKey: ["rabbit-hole-current"],
                                });
                                notifySnackbar({
                                  message:
                                    error instanceof RabbitHoleCreateError &&
                                    error.code === "rabbit_hole_active"
                                      ? translate("rabbit_hole_active_edition")
                                      : translate("rabbit_hole_create_error"),
                                });
                              });
                            }}
                            className="mt-4 border-t border-slate-200 pt-4 lg:mx-auto lg:max-w-[32rem]"
                          >
                            <div className="flex min-h-12 items-center rounded-[5px] border border-slate-300 bg-white focus-within:border-slate-500">
                              <input
                                type="text"
                                value={pickTopic}
                                onChange={(e) =>
                                  setPickingTopics((prev) => ({
                                    ...prev,
                                    [languageOption.id]: e.target.value,
                                  }))
                                }
                                placeholder={translate(
                                  "rabbit_hole_custom_topic_placeholder",
                                )}
                                className="min-w-0 flex-1 bg-transparent px-4 py-3 text-base text-slate-900 placeholder:text-slate-400 focus:outline-none"
                                data-testid={`overview-rabbit-hole-topic-input-${languageOption.id.toLowerCase()}`}
                              />
                              <button
                                type="submit"
                                disabled={
                                  !pickTopic.trim() || isCheckingClarification
                                }
                                className="flex h-11 w-12 shrink-0 items-center justify-center text-slate-700 transition hover:text-slate-950 disabled:text-slate-300"
                                aria-label={translate(
                                  "rabbit_hole_custom_topic_submit",
                                )}
                                data-testid={`overview-rabbit-hole-topic-submit-${languageOption.id.toLowerCase()}`}
                              >
                                {isCheckingClarification ? (
                                  <span className="text-base leading-none">
                                    …
                                  </span>
                                ) : (
                                  <ArrowRightIcon
                                    className="h-5 w-5"
                                    aria-hidden="true"
                                  />
                                )}
                              </button>
                            </div>
                          </form>
                          {clarification ? (
                            <TopicRefinementCard
                              refinement={clarification}
                              translate={translate}
                              onChooseTopic={(topic) =>
                                void handleClarificationChoice(
                                  languageOption.id,
                                  topic,
                                )
                              }
                              onKeepBroad={() =>
                                void handleKeepBroadTopic(
                                  languageOption.id,
                                  clarification.topic,
                                )
                              }
                              testIdPrefix={`overview-rabbit-hole-${languageOption.id.toLowerCase()}`}
                            />
                          ) : null}
                        </div>
                      )}

                      {isRabbitHoleCardActive(week) ? (
                        <div className="mt-4 min-w-0 lg:mx-auto lg:max-w-[18rem]">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleExpanded(languageOption.id);
                            }}
                            className="flex w-full min-w-0 items-center justify-between gap-3 text-left"
                            data-testid={`overview-rabbit-hole-expand-${languageOption.id.toLowerCase()}`}
                          >
                            <span className="min-w-0 break-words text-sm leading-5 text-slate-700">
                              {translate("overview_rabbit_hole_upcoming_title")}
                            </span>
                            <ChevronDownIcon
                              className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${
                                isExpanded ? "rotate-180" : ""
                              }`}
                            />
                          </button>

                          {isExpanded ? (
                            <div
                              className="mt-3 grid w-full min-w-0 max-w-full grid-cols-2 gap-3 overflow-hidden sm:grid-cols-3 lg:grid-cols-2"
                              data-testid={`overview-rabbit-hole-expanded-${languageOption.id.toLowerCase()}`}
                            >
                              {upcomingSlots.map((slot) => {
                                const isSummarySlot =
                                  "isSummary" in slot && slot.isSummary;
                                const slotEditionTitle = isSummarySlot
                                  ? {
                                      title: translate(
                                        "rabbit_hole_open_summary",
                                      ),
                                      pending: false,
                                    }
                                  : getRabbitHoleEditionTitle(
                                      slot.discovery?.topic,
                                    );
                                const effectiveSlotDiscovery = isSummarySlot
                                  ? undefined
                                  : resolveEffectiveRabbitHoleDiscovery({
                                      discovery: slot.discovery,
                                      generatedTexts: allTexts,
                                    });
                                const canOpen = isSummarySlot
                                  ? Boolean(week?.id)
                                  : Boolean(
                                      slot.discovery &&
                                        (slot.isCompleted ||
                                          slot.isToday ||
                                          slot.isTomorrow),
                                    );
                                const summaryProgressLabel = translate(
                                  "rabbit_hole_weekly_review",
                                );
                                const summaryDayLabel = translate(
                                  "rabbit_hole_open_summary",
                                );
                                const summaryActionLabel = translate(
                                  "rabbit_hole_open_summary",
                                );
                                return (
                                  <RabbitHoleEditionCover
                                    key={slot.index}
                                    topic={week.topic}
                                    title={slotEditionTitle.title}
                                    language={languageOption.id}
                                    languageLabel={
                                      OVERVIEW_NATIVE_LANGUAGE_LABELS[
                                        languageOption.id
                                      ] ||
                                      translate(
                                        languageOption.prettyTranslationKey,
                                      )
                                    }
                                    weekStart={week.week_start}
                                    weekEnd={week.week_end}
                                    dayIndex={slot.index}
                                    completed={week.read_discoveries ?? 0}
                                    total={week.total_discoveries ?? 6}
                                    dayLabel={
                                      isSummarySlot
                                        ? summaryDayLabel
                                        : translate("rabbit_hole_edition_day", {
                                            day: slot.index + 1,
                                          })
                                    }
                                    progressLabel={
                                      isSummarySlot
                                        ? summaryProgressLabel
                                        : slot.isToday
                                          ? `${translate("rabbit_hole_today_label")} · ${slot.dayLabel}`
                                          : slot.isTomorrow
                                            ? `${translate("rabbit_hole_tomorrow_label")} · ${slot.dayLabel}`
                                            : slot.dayLabel
                                    }
                                    verticalLabel={translate(
                                      "rabbit_hole_edition_vertical_label",
                                    )}
                                    dateRangeLabel={formatRabbitHoleEditionDateRange(
                                      week.week_start,
                                      week.week_end,
                                      appLanguage,
                                    )}
                                    isRead={
                                      isSummarySlot
                                        ? false
                                        : isRabbitHoleDiscoveryRead(
                                            effectiveSlotDiscovery,
                                          )
                                    }
                                    titlePending={
                                      isSummarySlot
                                        ? false
                                        : slotEditionTitle.pending
                                    }
                                    unavailableLabel={
                                      isSummarySlot
                                        ? summaryDayLabel
                                        : translate("rabbit_hole_generating")
                                    }
                                    actionLabel={
                                      isSummarySlot
                                        ? summaryActionLabel
                                        : undefined
                                    }
                                    onAction={
                                      isSummarySlot
                                        ? () =>
                                            void navigate({
                                              to: "/app/rabbit_hole",
                                              search: {
                                                weekId: week.id as number,
                                                summary: true,
                                              },
                                            })
                                        : canOpen && slot.discovery
                                          ? () =>
                                              void openRabbitHoleDiscovery({
                                                discovery: slot.discovery,
                                                languageSpace:
                                                  languageOption.id,
                                                generatedTexts: allTexts,
                                                queryClient,
                                                navigate,
                                                setLanguageSpace,
                                              })
                                          : undefined
                                    }
                                    variant="mini"
                                    testId={`overview-rabbit-hole-slot-${languageOption.id.toLowerCase()}-${slot.index}`}
                                  />
                                );
                              })}
                              <div className="col-span-2 border-t border-slate-100 pt-3">
                                <p className="text-sm leading-5 text-slate-500">
                                  {translate("rabbit_hole_weekly_review")}
                                </p>
                              </div>
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
            </div>
          </div>
        </>
      ) : null}
    </section>
  );
}

function FeaturedOverviewTask({
  task,
  title,
  subtitle,
  wordCount,
}: {
  task: OverviewTask;
  title: string;
  subtitle: string;
  wordCount: number | null;
}) {
  const { translate } = useTranslation();
  const content = (
    <>
      <h3
        className="break-words font-serif text-[22px] leading-tight tracking-[0.1px] text-slate-950 hyphens-auto sm:text-[24px] lg:text-[30px]"
        data-testid="overview-featured-daily-title"
      >
        {title}
      </h3>
      <p
        className="mt-1 min-h-[1.4rem] text-[1rem] leading-6 text-slate-600"
        data-testid="overview-featured-daily-subtitle"
      >
        {subtitle}
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[0.9rem] text-slate-500">
        {wordCount ? (
          <span>
            {translate("app_overview_word_count", {
              count: wordCount,
            })}
          </span>
        ) : null}
        <span>{task.detail}</span>
      </div>
    </>
  );

  return (
    <section
      className="border-t border-slate-200 pt-5"
      data-testid="overview-featured-daily"
    >
      <h2 className="text-sm font-medium text-slate-500">
        {translate("app_overview_today_text_headline")}
      </h2>
      {task.navigationTarget ? (
        <Link
          {...task.navigationTarget}
          onClick={task.onBeforeOpen}
          className="mt-2 block min-w-0 text-left"
          data-testid="overview-featured-daily-open"
        >
          {content}
        </Link>
      ) : (
        <button
          type="button"
          onClick={() => void task.onOpen?.()}
          className="mt-2 block min-w-0 text-left"
          data-testid="overview-featured-daily-open"
        >
          {content}
        </button>
      )}
    </section>
  );
}

function ChecklistSection({
  title,
  children,
  subdued = false,
}: {
  title: string;
  children: ReactNode;
  subdued?: boolean;
}) {
  return (
    <section>
      <h3
        className={
          subdued
            ? "text-[0.78rem] font-medium tracking-[-0.01em] text-slate-400"
            : "text-[0.82rem] font-medium tracking-[-0.01em] text-slate-500"
        }
      >
        {title}
      </h3>
      <div className="mt-2">{children}</div>
    </section>
  );
}

function OverviewTaskSections({
  recommendedTasks,
  continueTasks,
  compact = false,
  showRecommendedHeading = true,
}: {
  recommendedTasks: OverviewTask[];
  continueTasks: OverviewTask[];
  compact?: boolean;
  showRecommendedHeading?: boolean;
}) {
  const { translate } = useTranslation();

  return (
    <div className={compact ? "space-y-5" : "space-y-8"}>
      {recommendedTasks.length > 0 ? (
        showRecommendedHeading ? (
          <ChecklistSection title={translate("app_overview_recommended")}>
            {recommendedTasks.map((task) => (
              <ChecklistAction key={task.id} {...task} compact={compact} />
            ))}
          </ChecklistSection>
        ) : (
          <section>
            {recommendedTasks.map((task) => (
              <ChecklistAction key={task.id} {...task} compact={compact} />
            ))}
          </section>
        )
      ) : null}

      {continueTasks.length > 0 ? (
        <ChecklistSection
          title={translate("app_overview_continue_label")}
          subdued
        >
          {continueTasks.map((task) => (
            <ChecklistAction key={task.id} {...task} compact={compact} />
          ))}
        </ChecklistSection>
      ) : null}
    </div>
  );
}

type OverviewTask = {
  id: string;
  checked: boolean;
  busy?: boolean;
  onToggle: () => void;
  onOpen?: () => void | Promise<void>;
  onBeforeOpen?: () => void;
  label: string;
  detail: string;
  generatedText?: GeneratedText;
  navigationTarget?: {
    to: "/app/reeprise/$generatedTextId";
    params: {
      generatedTextId: string;
    };
    search: {
      chapter: undefined;
      focusSentence: undefined;
      focusToken: undefined;
      focusWord: undefined;
      jumpTo: undefined;
    };
  };
  testId?: string;
  section: "recommended" | "continue";
  kind: "daily" | "continue";
};

function buildGeneratedTextNavigationTarget(generatedTextId: string) {
  return {
    to: "/app/reeprise/$generatedTextId" as const,
    params: {
      generatedTextId,
    },
    search: {
      chapter: undefined,
      focusSentence: undefined,
      focusToken: undefined,
      focusWord: undefined,
      jumpTo: undefined,
    },
  };
}

function ChecklistAction({
  checked,
  busy,
  compact = false,
  onToggle,
  onOpen,
  onBeforeOpen,
  label,
  detail,
  navigationTarget,
  testId,
}: {
  checked: boolean;
  busy?: boolean;
  compact?: boolean;
  onToggle: () => void;
  onOpen?: () => void | Promise<void>;
  onBeforeOpen?: () => void;
  label: string;
  detail: string;
  navigationTarget?: OverviewTask["navigationTarget"];
  testId?: string;
}) {
  const { translate } = useTranslation();
  const content = (
    <>
      <span className="min-w-0">
        <span
          className={`block font-serif font-normal tracking-[0.1px] ${
            testId?.includes("overview-open-continue")
              ? "text-slate-700"
              : "text-slate-950"
          } ${compact ? "text-[0.98rem] leading-5" : "text-[1.02rem] leading-6"}`}
        >
          {label}
        </span>
        <span
          className={`block ${
            testId?.includes("overview-open-continue")
              ? "text-slate-400"
              : "text-slate-500"
          } ${compact ? "text-[0.82rem] leading-5" : "text-sm leading-6"}`}
        >
          {busy ? "…" : detail}
        </span>
      </span>
      <ChevronRightIcon
        className={`mt-1 h-4 w-4 shrink-0 ${
          testId?.includes("overview-open-continue")
            ? "text-slate-300"
            : "text-slate-400"
        }`}
      />
    </>
  );

  return (
    <div
      className={`flex items-start gap-3 transition ${
        compact ? "py-2.5" : "py-3"
      }`}
      data-testid={testId}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-label={
          checked
            ? translate("app_overview_checkbox_checked")
            : translate("app_overview_checkbox_unchecked")
        }
        className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-slate-500 transition hover:text-slate-950"
        data-testid={testId ? `${testId}-toggle` : undefined}
      >
        {checked ? (
          <CheckCircleIcon className="h-5 w-5 text-slate-950" />
        ) : (
          <span className="block h-4 w-4 rounded-full border border-slate-400 bg-transparent" />
        )}
      </button>
      <div className="min-w-0 flex-1">
        {navigationTarget ? (
          <Link
            {...navigationTarget}
            onClick={onBeforeOpen}
            className="flex w-full min-w-0 items-start justify-between gap-3 text-left"
            data-testid={testId ? `${testId}-open` : undefined}
          >
            {content}
          </Link>
        ) : (
          <button
            type="button"
            onClick={() => void onOpen?.()}
            className="flex w-full min-w-0 items-start justify-between gap-3 text-left"
            data-testid={testId ? `${testId}-open` : undefined}
          >
            {content}
          </button>
        )}
      </div>
    </div>
  );
}

function buildOverviewTasks(args: {
  languageOption: (typeof TEXT_LANGUAGES)[number];
  allTexts: GeneratedText[];
  backgroundDailyText?: GeneratedText;
  todayIso: string;
  translate: (
    key: string,
    variables?: Record<string, string | number>,
  ) => string;
  manualChecklistState: Record<string, boolean>;
  setManualChecklistState: (value: Record<string, boolean>) => void;
  setLanguageSpace: (
    value: "ENGLISH" | "FRENCH" | "GERMAN" | "ITALIAN",
  ) => void;
  navigate: ReturnType<typeof useNavigate>;
  queryClient: ReturnType<typeof useQueryClient>;
  dailyTextCreate: ReturnType<typeof useDailyTextCreate>;
  openingDailyLanguageId: string | null;
  setOpeningDailyLanguageId: (value: string | null) => void;
}): OverviewTask[] {
  const {
    languageOption,
    allTexts,
    backgroundDailyText,
    todayIso,
    translate,
    manualChecklistState,
    setManualChecklistState,
    setLanguageSpace,
    navigate,
    queryClient,
    dailyTextCreate,
    openingDailyLanguageId,
    setOpeningDailyLanguageId,
  } = args;
  const languageLabel =
    OVERVIEW_NATIVE_LANGUAGE_LABELS[languageOption.id] ||
    translate(languageOption.prettyTranslationKey);
  const todayDailyText =
    findTodayDailyText(allTexts, languageOption.id, todayIso) ??
    resolveSameDayDailyText(backgroundDailyText, todayIso, languageOption.id);
  const continueText = findContinueText(allTexts, languageOption.id, todayIso);
  const dailyReadAutomatically = Boolean(
    todayDailyText && todayDailyText.view_count > 0,
  );
  const continueCheckedAutomatically = Boolean(
    continueText && continueText.view_count > 0,
  );
  const readDailyTaskKey = buildChecklistKey({
    day: todayIso,
    languageId: languageOption.id,
    taskId: "read-daily",
  });

  const tasks: OverviewTask[] = [
    {
      id: `daily-${languageOption.id.toLowerCase()}`,
      section: "recommended",
      kind: "daily",
      checked:
        dailyReadAutomatically ||
        manualChecklistState[readDailyTaskKey] === true,
      busy: openingDailyLanguageId === languageOption.id,
      onToggle: () =>
        toggleChecklistValue({
          key: readDailyTaskKey,
          state: manualChecklistState,
          setState: setManualChecklistState,
        }),
      onBeforeOpen: todayDailyText
        ? () => {
            setLanguageSpace(
              languageOption.id as "ENGLISH" | "FRENCH" | "GERMAN" | "ITALIAN",
            );
            primeGeneratedTextDetailsQuery(queryClient, todayDailyText);
          }
        : undefined,
      navigationTarget: todayDailyText
        ? buildGeneratedTextNavigationTarget(String(todayDailyText.id))
        : undefined,
      onOpen: todayDailyText
        ? undefined
        : async () => {
            setLanguageSpace(
              languageOption.id as "ENGLISH" | "FRENCH" | "GERMAN" | "ITALIAN",
            );
            setOpeningDailyLanguageId(languageOption.id);
            try {
              const created = await dailyTextCreate.mutateAsync({
                language: languageOption.id,
              });
              primeGeneratedTextDetailsQuery(queryClient, created);
              void navigate(
                buildGeneratedTextNavigationTarget(String(created.id)),
              );
            } finally {
              setOpeningDailyLanguageId(null);
            }
          },
      label: resolveOverviewDailyDisplayTitle(todayDailyText) || "",
      detail: languageLabel,
      generatedText: todayDailyText,
      testId: `overview-open-daily-${languageOption.id.toLowerCase()}`,
    },
  ];

  if (!todayDailyText || !tasks[0].label.trim()) {
    tasks.shift();
  }

  if (continueText) {
    tasks.push({
      id: `continue-${languageOption.id.toLowerCase()}`,
      section: "continue",
      kind: "continue",
      checked: continueCheckedAutomatically,
      onToggle: () => undefined,
      onBeforeOpen: () => {
        setLanguageSpace(
          languageOption.id as "ENGLISH" | "FRENCH" | "GERMAN" | "ITALIAN",
        );
        primeGeneratedTextDetailsQuery(queryClient, continueText);
      },
      navigationTarget: buildGeneratedTextNavigationTarget(
        String(continueText.id),
      ),
      label:
        resolveOverviewDailyDisplayTitle(continueText) ||
        translate("app_overview_continue_reading_headline"),
      detail: languageLabel,
      generatedText: continueText,
      testId: `overview-open-continue-${languageOption.id.toLowerCase()}`,
    });
  }

  return tasks;
}

function buildChecklistKey({
  day,
  languageId,
  taskId,
}: {
  day: string;
  languageId: string;
  taskId: string;
}) {
  return `${day}:${languageId}:${taskId}`;
}

function toggleChecklistValue({
  key,
  state,
  setState,
}: {
  key: string;
  state: Record<string, boolean>;
  setState: (value: Record<string, boolean>) => void;
}) {
  setState({
    ...state,
    [key]: !state[key],
  });
}

function findTodayDailyText(
  texts: GeneratedText[],
  languageId: string,
  todayIso: string,
) {
  return sortGeneratedTexts(
    texts.filter(
      (text) =>
        text.language === languageId &&
        text.origin === "DAILY_NUDGE" &&
        getLocalIsoDateFromTimestamp(text.created_at) === todayIso,
    ),
  )[0];
}

function resolveSameDayDailyText(
  text: GeneratedText | undefined,
  todayIso: string,
  languageId: string,
) {
  if (!text) {
    return undefined;
  }
  if (text.origin !== "DAILY_NUDGE") {
    return undefined;
  }
  if (text.language !== languageId) {
    return undefined;
  }
  if (getLocalIsoDateFromTimestamp(text.created_at) !== todayIso) {
    return undefined;
  }
  return text;
}

function findContinueText(
  texts: GeneratedText[],
  languageId: string,
  todayIso: string,
) {
  const candidates = sortGeneratedTexts(
    texts.filter((text) => {
      if (text.language !== languageId) {
        return false;
      }
      if (
        text.origin === "DAILY_NUDGE" &&
        getLocalIsoDateFromTimestamp(text.created_at) === todayIso
      ) {
        return false;
      }
      if (text.status !== "COMPLETE") {
        return false;
      }
      return text.view_count > 0 || text.learning_progress > 0;
    }),
  );
  return candidates[0];
}

const isRabbitHoleCardActive = isRabbitHoleWeekActive;

function getCardSortTier(
  week: RabbitHoleWeek | undefined,
  todayIso: string,
): number {
  if (!week || week.status === "none") return 4;
  if (!isRabbitHoleCardActive(week)) return 3;
  const todaysDiscovery = findRabbitHoleDiscoveryForDate(week, todayIso);
  if (!todaysDiscovery) return 1;
  return todaysDiscovery.view_count > 0 ? 2 : 0;
}

function buildAllRabbitHoleSlots(
  week: RabbitHoleWeek,
  todayIso: string,
  appLanguage: string,
) {
  if (!week.discoveries?.length) {
    return [];
  }

  const dateFormat = new Intl.DateTimeFormat(
    appLanguage === "GERMAN" ? "de-DE" : "en-US",
    { weekday: "long", day: "numeric", month: "long" },
  );

  const slotData = week.discoveries
    .filter((d) => typeof d.rabbit_hole_day_index === "number")
    .map((d) => {
      const slotDate = new Date(`${week.week_start}T00:00:00`);
      slotDate.setDate(slotDate.getDate() + d.rabbit_hole_day_index);
      const iso = getLocalIsoDate(slotDate);
      const isCompleted = d.status === "COMPLETE" && d.has_content === true;
      const isToday = iso === todayIso;
      const isFuture = iso > todayIso;
      const tomorrowIso = getLocalIsoDate(
        new Date(new Date().setDate(new Date().getDate() + 1)),
      );
      const isTomorrow = iso === tomorrowIso;
      return {
        index: d.rabbit_hole_day_index,
        iso,
        dayLabel: dateFormat.format(slotDate),
        title: (d.topic || "").replace(/^rabbit hole – /, "").trim(),
        discovery: d,
        isCompleted,
        isToday,
        isFuture,
        isTomorrow,
      };
    })
    .sort((a, b) => a.index - b.index);

  return [
    ...slotData,
    {
      index: 6,
      iso: week.week_end,
      dayLabel: week.week_end,
      title: "",
      discovery: undefined,
      isCompleted: false,
      isToday: false,
      isFuture: false,
      isTomorrow: false,
      isSummary: true,
    },
  ];
}

function TopicRefinementCard({
  refinement,
  translate,
  onChooseTopic,
  onKeepBroad,
  testIdPrefix,
}: {
  refinement: RabbitHoleTopicRefinement;
  translate: (key: string, vars?: Record<string, string | number>) => string;
  onChooseTopic: (topic: string) => void | Promise<void>;
  onKeepBroad: () => void | Promise<void>;
  testIdPrefix: string;
}) {
  return (
    <section
      className="mt-4 rounded-2xl border border-slate-200 bg-slate-50/80 p-4"
      data-testid={`${testIdPrefix}-refinement`}
    >
      <p className="text-sm font-medium text-slate-700">
        {refinement.senseLabel
          ? translate("rabbit_hole_refine_title_sense", {
              topic: refinement.topic,
              sense: refinement.senseLabel,
            })
          : translate("rabbit_hole_refine_title")}
      </p>
      <p className="mt-1 text-sm text-slate-500">
        {translate("rabbit_hole_refine_body", {
          topic: refinement.topic,
          sense: refinement.senseLabel ?? "",
        })}
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        {refinement.suggestions.map((suggestion) => (
          <button
            key={suggestion}
            type="button"
            onClick={() => void onChooseTopic(suggestion)}
            className="rounded-full border border-slate-300 bg-white px-4 py-2 text-base text-slate-700 transition hover:bg-slate-100"
            data-testid={`${testIdPrefix}-refinement-option-${suggestion
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, "-")
              .replace(/^-+|-+$/g, "")}`}
          >
            {suggestion}
          </button>
        ))}
      </div>
      <button
        type="button"
        onClick={() => void onKeepBroad()}
        className="mt-3 text-sm font-medium text-slate-600 transition hover:text-slate-900"
      >
        {translate("rabbit_hole_refine_keep_broad")}
      </button>
    </section>
  );
}
